import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Comment } from '../../../types';
import MarkdownDisplay from '../../Common/MarkdownDisplay';
import MarkdownEditor from '../../Common/MarkdownEditor';
import { Avatar, AvatarFallback, AvatarImage } from '../../Common/avatar';

interface CommentItemProps {
  comment: Comment;
  // Add handlers for update and delete
  onUpdate: (commentId: string, newContent: string) => Promise<void>;
  onDelete: (commentId: string) => Promise<void>;
}

const CommentItem: React.FC<CommentItemProps> = ({ comment, onUpdate, onDelete }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(comment.content);
  const [isProcessing, setIsProcessing] = useState(false); // To disable buttons during action

  // Use created_at for timestamp display
  const formattedTimestamp = new Date(comment.created_at).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  // const timeAgo = formatDistanceToNow(new Date(comment.timestamp), { addSuffix: true, locale: fr });

  const handleEditToggle = () => {
    if (isEditing) {
      // Reset content if canceling edit
      setEditedContent(comment.content);
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    if (editedContent === comment.content) {
      setIsEditing(false); // No changes, just exit edit mode
      return;
    }
    setIsProcessing(true);
    try {
      await onUpdate(comment.id, editedContent);
      setIsEditing(false); // Exit edit mode on success
    } catch (error) {
      console.error("Failed to update comment:", error);
      // Keep editing mode open on error to allow retry
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    setIsProcessing(true);
    try {
      await onDelete(comment.id);
      // Component will likely unmount if delete is successful
    } catch (error) {
      console.error("Failed to delete comment:", error);
    } finally {
      // No need to set isProcessing false if component unmounts
      // but set it just in case delete fails and component remains
       setIsProcessing(false);
    }
  };


  const displayName =
    comment.owner?.name?.trim() ||
    comment.owner?.email ||
    (comment.owner_id ? `User (${comment.owner_id.substring(0, 6)}...)` : '?');
  const avatarInitial = comment.owner?.name?.trim()
    ? comment.owner.name.charAt(0).toUpperCase()
    : comment.owner?.email
      ? comment.owner.email.charAt(0).toUpperCase()
      : '?';
  const avatarUrl = comment.owner?.avatar_url?.trim() || null;

  return (
    <div className="flex space-x-3 py-4 border-b border-border last:border-b-0">
      <div className="flex-shrink-0">
        <Avatar className="h-8 w-8">
          {avatarUrl ? (
            <AvatarImage src={avatarUrl} alt={displayName} />
          ) : null}
          <AvatarFallback className="text-xs font-semibold text-muted-foreground">
            {avatarInitial}
          </AvatarFallback>
        </Avatar>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex justify-between items-center">
            <div>
                <p className="text-sm font-medium text-foreground">
                  {displayName}
                </p>
                <p className="text-sm text-muted-foreground">
                  {/* Use created_at for dateTime attribute */}
                  <time dateTime={comment.created_at}>{formattedTimestamp}</time>
                </p>
            </div>
             {/* Action Buttons (only show if not editing) */}
             {!isEditing && (
                <div className="flex-shrink-0 space-x-2">
                    <button
                        onClick={handleEditToggle}
                        disabled={isProcessing}
                        className="text-xs text-primary hover:text-primary/80 disabled:opacity-50"
                    >
                        {t('global.edit')}
                    </button>
                    <button
                        onClick={handleDelete}
                        disabled={isProcessing}
                        className="text-xs text-red-600 hover:text-red-500 disabled:opacity-50"
                    >
                        {t('global.delete')}
                    </button>
                </div>
             )}
        </div>

        {/* Display Content or Edit Form */}
        <div className="mt-2 text-sm text-foreground">
          {isEditing ? (
            <div className="space-y-2">
              <MarkdownEditor
                value={editedContent}
                onChange={(val) => setEditedContent(val || '')}
                preview="live"
                height={150}
                disabled={isProcessing}
                className="block w-full"
              />
              <div className="flex space-x-2">
                <button
                  onClick={handleSave}
                  disabled={isProcessing || editedContent === comment.content}
                  className="px-3 py-1 bg-primary text-background rounded-md shadow text-xs hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
                >
                  {isProcessing ? t('global.saving') : t('global.save')}
                </button>
                <button
                  onClick={handleEditToggle}
                  disabled={isProcessing}
                  className="px-3 py-1 border border-input bg-background text-foreground rounded-md shadow text-xs hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
                >
                  {t('global.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <MarkdownDisplay content={comment.content} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentItem;
